import { inject, Injectable, Injector } from '@angular/core';
import { FirebaseMessaging, Importance, Notification as PushMessage } from '@capacitor-firebase/messaging';
import { LocalNotifications } from '@capacitor/local-notifications';
import { AlertController, NavController, Platform } from '@ionic/angular/standalone';
import { TripRatingService } from '@core/services/trip-rating/trip-rating.service';
import { NotificationsStore, supportTicketKeys } from '@shared/stores';
import { circuitKeys } from '@shared/stores/circuit.keys';
import { QueryClient } from '@tanstack/angular-query-experimental';

import { AuthService } from './auth.service';
import { DataService } from './data.service';
import { PushData, pushRoute } from './push-route';
import { PushRegistrationService } from './push-registration.service';

/** TEC-327: pushes que avisan que terminó un viaje del Circuito TEC. */
const CIRCUIT_TRIP_FINISHED = ['circuit_completed', 'circuit_unregistered_alight'];

/** Canal de Android de los push; el servidor lo manda como `channel_id`. */
const ANDROID_CHANNEL_ID = 'default';

/** Ícono blanco de la barra de estado para la notificación local (`res/drawable`). */
const ANDROID_SMALL_ICON = 'ic_stat_notification';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private auth = inject(AuthService);
  private platform = inject(Platform);
  private alertCtrl = inject(AlertController);
  private dataService = inject(DataService);
  private injector = inject(Injector);
  private queryClient = inject(QueryClient);
  private registration = inject(PushRegistrationService);

  #navCtrl: NavController = inject(NavController);
  #initialized = false;

  /**
   * Listeners de push (FCM) lo antes posible: los plugins retienen el toque que abrió
   * el app hasta que hay listener, así que un arranque en frío no lo pierde. Con sesión
   * y permiso ya concedido, vuelve a registrar el token sin preguntar nada.
   */
  async initPush(): Promise<void> {
    if (this.#initialized) return;

    const platformReady = await this.platform.ready();
    if (!platformReady || !this.platform.is('hybrid')) return;

    this.#initialized = true;

    await FirebaseMessaging.addListener('notificationReceived', ({ notification }) =>
      this.onForegroundPush(notification)
    );
    await FirebaseMessaging.addListener('notificationActionPerformed', ({ notification }) =>
      this.openFromPush(pushData(notification))
    );
    await FirebaseMessaging.addListener('tokenReceived', ({ token }) => this.registration.tokenChanged(token));

    if (this.platform.is('android')) {
      await FirebaseMessaging.createChannel({
        id: ANDROID_CHANNEL_ID,
        name: 'Notificaciones',
        importance: Importance.High,
      });
      await LocalNotifications.addListener('localNotificationActionPerformed', ({ notification }) =>
        this.openFromPush(notification.extra as PushData | undefined)
      );
    }

    const user = await this.auth.getUser();
    if (user && (await this.permissionGranted())) {
      await this.registration.enable();
    }
  }

  // Se resuelve al vuelo: initPush corre en el arranque y el store no debe
  // instanciarse (ni montar su query) antes de que haga falta.
  private notificationsStore(): NotificationsStore {
    return this.injector.get(NotificationsStore);
  }

  private tripRatingService(): TripRatingService {
    return this.injector.get(TripRatingService);
  }

  /**
   * Push con el app abierta. iOS ya lo muestra (presentationOptions); Android no, así
   * que se presenta como notificación local con la misma data para que el toque abra
   * la misma pantalla.
   */
  private async onForegroundPush(notification: PushMessage): Promise<void> {
    const data = pushData(notification);

    // TEC-215: el badge del Wallet se prende sin pedir la lista.
    this.notificationsStore().markPushReceived();
    // Respuesta de soporte: la lista de tickets queda vieja (estado Pendiente/Atendido).
    // Sin refetch: se pide al volver a mostrarla. El chat abierto ya la recibe por Pusher.
    if (data?.['type'] === 'ticket_message') {
      this.queryClient.invalidateQueries({ queryKey: supportTicketKeys.all, refetchType: 'none' });
    }
    // TEC-327: bajó del Circuito TEC (escaneo, registro manual o cierre de la vuelta).
    // El boleto abierto pasa a COMPLETADO y sale el recordatorio de calificar.
    if (data?.['type'] && CIRCUIT_TRIP_FINISHED.includes(data['type'])) {
      this.queryClient.invalidateQueries({ queryKey: circuitKeys.all });
      this.tripRatingService().checkPendingRatings();
    }

    if (this.platform.is('android')) {
      try {
        await LocalNotifications.schedule({
          notifications: [
            {
              id: Math.floor(Date.now() % 2_147_483_647),
              title: notification.title ?? '',
              body: notification.body ?? '',
              extra: data,
              channelId: ANDROID_CHANNEL_ID,
              smallIcon: ANDROID_SMALL_ICON,
            },
          ],
        });
      } catch {
        // Sin permiso de notificaciones no hay banner; el badge ya quedó prendido.
      }
    }
  }

  private async openFromPush(data: PushData | undefined): Promise<void> {
    // Mark notification as read if we have the notification_id and the user is authenticated.
    // Skipping when unauthenticated is intentional — the destination route guards will redirect to login.
    if (data?.['notification_id']) {
      const user = await this.auth.getUser();
      if (user) {
        this.notificationsStore().markAsRead(data['notification_id']);
      }
    }

    // Respuesta de soporte: si su chat ya estaba abierto, navegar a la misma URL no recrea la
    // página. Se relee el hilo; sin cancelar la lectura que ya disparó el resume del app.
    if (data?.['type'] === 'ticket_message' && data['ticket_id']) {
      this.queryClient.invalidateQueries(
        { queryKey: supportTicketKeys.thread(data['ticket_id']) },
        { cancelRefetch: false }
      );
    }

    const route = pushRoute(data);
    if (route.ticketSearch) {
      this.dataService.setData('ticket-search', route.ticketSearch);
    }
    this.#navCtrl.navigateRoot(route.path);
    if (data?.['type'] && CIRCUIT_TRIP_FINISHED.includes(data['type'])) {
      this.tripRatingService().checkPendingRatings();
    }
  }

  /**
   * Vincula esta instalación al usuario: pide permiso (si ya se concedió o se negó no
   * vuelve a preguntar) y registra el token de FCM en el API.
   */
  async attachUser(user: { id: number | string } | null): Promise<void> {
    if (!user) return;

    const platformReady = await this.platform.ready();
    if (!platformReady || !this.platform.is('hybrid')) return;

    // initPush es idempotente: garantiza los listeners antes del registro.
    await this.initPush();

    const { receive } = await FirebaseMessaging.requestPermissions();
    if (receive !== 'granted') return;

    await this.registration.enable();
  }

  /**
   * TEC-225: estado actual del permiso de notificaciones del OS.
   * null = no aplica (web) o el plugin no pudo responder.
   */
  async getPermissionStatus(): Promise<boolean | null> {
    if (!this.platform.is('hybrid')) return null;

    try {
      return await this.permissionGranted();
    } catch {
      return null;
    }
  }

  /**
   * TEC-225: re-solicita el permiso del OS (banner de la pantalla de
   * Notificaciones). Si el usuario lo negó permanentemente, el OS ya no
   * muestra el diálogo y esto regresa false — el banner lo explica.
   */
  async promptPermission(): Promise<boolean> {
    if (!this.platform.is('hybrid')) return false;

    try {
      const { receive } = await FirebaseMessaging.requestPermissions();
      const granted = receive === 'granted';
      if (granted && (await this.auth.getUser())) {
        await this.registration.enable();
      }
      return granted;
    } catch {
      return false;
    }
  }

  private async permissionGranted(): Promise<boolean> {
    const { receive } = await FirebaseMessaging.checkPermissions();
    return receive === 'granted';
  }

  async showAlert(title: string = 'Notificación', msg: string = '', task: string = 'OK') {
    const alert = await this.alertCtrl.create({
      header: title,
      subHeader: msg,
      buttons: [
        {
          text: `${task}`,
          handler: () => {
            // E.g: Navigate to a specific screen
          },
        },
      ],
    });
    alert.present();
  }
}

/** `data` del push: en iOS también trae las llaves de APNs/FCM, que nadie lee. */
function pushData(notification: PushMessage): PushData | undefined {
  return (notification.data ?? undefined) as PushData | undefined;
}
